import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SafeAvatarImage } from '@/components/ui/safe-avatar-image';
import { getProxiedImageUrl } from '@/utils/imageProxy';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { 
  CheckCircle, 
  Users, 
  Video, 
  Heart,
  ExternalLink,
  UserPlus,
  Link as LinkIcon,
  MapPin,
  Mail,
  Phone,
  Globe,
  BarChart3
} from 'lucide-react';
import { Creator } from '@/hooks/useCreators';
import { EditableBioSection } from './EditableBioSection';
import { useTalentBio } from '@/hooks/useTalentBio';

// Platform icons as simple SVG components
const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const YouTubeIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

interface CreatorDetailModalProps {
  creator: Creator | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInviteToDeal?: (creator: Creator) => void;
}

const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

const getPlatformIcon = (platform: string) => {
  switch (platform) {
    case 'tiktok':
      return <TikTokIcon />;
    case 'instagram':
      return <InstagramIcon />;
    case 'youtube':
      return <YouTubeIcon />;
    default:
      return null;
  }
};

const getPlatformColor = (platform: string): string => {
  switch (platform) {
    case 'tiktok':
      return 'bg-[#00f2ea]/10 text-[#00f2ea]';
    case 'instagram':
      return 'bg-gradient-to-r from-[#833ab4]/10 via-[#fd1d1d]/10 to-[#fcb045]/10 text-[#fd1d1d]';
    case 'youtube':
      return 'bg-[#ff0000]/10 text-[#ff0000]';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

const getPlatformUrl = (platform: string, username: string): string => {
  switch (platform) {
    case 'tiktok':
      return `https://www.tiktok.com/@${username}`;
    case 'instagram':
      return `https://www.instagram.com/${username}`;
    case 'youtube':
      return `https://www.youtube.com/channel/${username}`;
    default:
      return '#';
  }
};

export const CreatorDetailModal: React.FC<CreatorDetailModalProps> = ({
  creator,
  open,
  onOpenChange,
  onInviteToDeal,
}) => {
  const bioHook = useTalentBio({
    talentType: 'creator',
    talentId: creator?.id || '',
    platform: creator?.platform,
    name: creator?.display_name || creator?.username || '',
    followerCount: creator?.follower_count,
    videoCount: creator?.video_count,
    existingBio: creator?.bio,
  });

  if (!creator) return null;

  const hasExternalLinks = creator.external_links && creator.external_links.length > 0;
  const hasDemographics = creator.audience_demographics?.countries && creator.audience_demographics.countries.length > 0;
  const hasContactInfo = creator.email || creator.contact_phone;
  const hasMetadata = creator.category || creator.region || creator.business_category;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0 overflow-hidden">
        <ScrollArea className="max-h-[90vh]">
          {/* Header with platform gradient */}
          <div className="relative">
            <div className={`h-32 ${
              creator.platform === 'tiktok' ? 'bg-gradient-to-r from-[#00f2ea]/30 to-[#ff0050]/30' :
              creator.platform === 'instagram' ? 'bg-gradient-to-r from-[#833ab4]/30 via-[#fd1d1d]/30 to-[#fcb045]/30' :
              'bg-gradient-to-r from-[#ff0000]/30 to-[#ff0000]/20'
            }`} />
            <div className="absolute -bottom-12 left-6">
              <Avatar className="h-24 w-24 border-4 border-background shadow-lg">
                <SafeAvatarImage src={getProxiedImageUrl(creator.avatar_url)} className="object-cover" />
                <AvatarFallback className="text-2xl">
                  {creator.display_name[0] || creator.username[0]}
                </AvatarFallback>
              </Avatar>
            </div>
            {/* Platform badge */}
            <div className="absolute top-4 right-4">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${getPlatformColor(creator.platform)}`}>
                {getPlatformIcon(creator.platform)}
                <span className="font-medium capitalize">{creator.platform}</span>
              </div>
            </div>
          </div>

          <div className="pt-14 px-6 pb-6 space-y-6">
            <DialogHeader>
              <div className="flex items-center gap-2">
                <DialogTitle className="text-2xl font-bold">
                  {creator.display_name}
                </DialogTitle>
                {creator.is_verified && (
                  <CheckCircle className="h-5 w-5 text-primary fill-primary/20" />
                )}
              </div>
              <p className="text-muted-foreground">@{creator.username}</p>
              
              {/* Metadata badges */}
              {hasMetadata && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {creator.category && (
                    <Badge variant="secondary" className="text-xs">
                      {creator.category}
                    </Badge>
                  )}
                  {creator.business_category && creator.business_category !== creator.category && (
                    <Badge variant="outline" className="text-xs">
                      {creator.business_category}
                    </Badge>
                  )}
                  {creator.region && (
                    <Badge variant="outline" className="text-xs">
                      <MapPin className="h-3 w-3 mr-1" />
                      {creator.region}
                    </Badge>
                  )}
                </div>
              )}
            </DialogHeader>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-muted/50 rounded-lg p-4 text-center">
                <Users className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
                <p className="text-2xl font-bold">{formatNumber(creator.follower_count)}</p>
                <p className="text-xs text-muted-foreground">Followers</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-4 text-center">
                <Users className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
                <p className="text-2xl font-bold">{formatNumber(creator.following_count)}</p>
                <p className="text-xs text-muted-foreground">Following</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-4 text-center">
                <Video className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
                <p className="text-2xl font-bold">{formatNumber(creator.video_count)}</p>
                <p className="text-xs text-muted-foreground">Videos</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-4 text-center">
                <Heart className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
                <p className="text-2xl font-bold">{formatNumber(creator.like_count)}</p>
                <p className="text-xs text-muted-foreground">Likes</p>
              </div>
            </div>

            {/* External Links */}
            {hasExternalLinks && (
              <>
                <Separator />
                <div className="space-y-3">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <LinkIcon className="h-4 w-4" />
                    Links
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {creator.external_links!.map((link, index) => (
                      <Button
                        key={index}
                        variant="outline"
                        size="sm"
                        asChild
                        className="text-xs"
                      >
                        <a href={link.url} target="_blank" rel="noopener noreferrer">
                          <Globe className="h-3 w-3 mr-1" />
                          {link.title || 'Link'}
                          <ExternalLink className="h-3 w-3 ml-1" />
                        </a>
                      </Button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Contact Info */}
            {hasContactInfo && (
              <>
                <Separator />
                <div className="space-y-3">
                  <h4 className="text-sm font-medium">Contact</h4>
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    {creator.email && (
                      <a 
                        href={`mailto:${creator.email}`} 
                        className="flex items-center gap-1 hover:text-foreground transition-colors"
                      >
                        <Mail className="h-4 w-4" />
                        {creator.email}
                      </a>
                    )}
                    {creator.contact_phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="h-4 w-4" />
                        {creator.contact_phone}
                      </span>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Audience Demographics */}
            {hasDemographics && (
              <>
                <Separator />
                <div className="space-y-3">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    Audience Demographics
                  </h4>
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">Top Countries</p>
                    <div className="space-y-2">
                      {creator.audience_demographics!.countries!.slice(0, 5).map((country, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                            <div 
                              className="h-full bg-primary rounded-full transition-all"
                              style={{ width: `${country.percentage}%` }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground w-24 text-right">
                            {country.country}
                          </span>
                          <span className="text-xs font-medium w-12 text-right">
                            {country.percentage.toFixed(1)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Bio */}
            <Separator />
            <EditableBioSection
              bio={bioHook.bio}
              isEdited={bioHook.isEdited}
              generatedBio={bioHook.generatedBio}
              isLoading={bioHook.isLoading}
              isGenerating={bioHook.isGenerating}
              isSaving={bioHook.isSaving}
              onGenerate={bioHook.generateBio}
              onSave={bioHook.saveBio}
              onReset={bioHook.resetToGenerated}
            />

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button className="flex-1" asChild>
                <a 
                  href={creator.profile_url || getPlatformUrl(creator.platform, creator.username)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Profile
                </a>
              </Button>
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  if (creator && onInviteToDeal) {
                    onInviteToDeal(creator);
                    onOpenChange(false);
                  }
                }}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Invite to Deal
              </Button>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
